import { ChangeEvent, FormEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react';
import { Popover } from '@mui/material';

import lang from '../../lib/language';
import { ControlError } from '../../lib/util/ValidationSchemes/ValidationTypes';

import InfoIcon from '../../assets/img/icon-info-outline.svg';
import InputErrorIcon from '../../assets/img/input-error-icon.svg';

import styles from './InputControl.module.scss';

type Props = {
  name: string;
  placeholder: string;
  id?: string;
  errorId?: string;
  ariaLabel?: string;
  errorAriaLabel?: string;
  value?: string | number;
  label?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string | number) => void;
  type?: 'email' | 'text' | 'number' | 'password';
  min?: number;
  errors?: ControlError[];
  disabled?: boolean;
  onBlur?: (value: string | number) => void;
  className?: string;
  showInlineHelp?: boolean;
  withMinimumValue?: boolean;
  helperText?: string;
  onSubmit?: () => void;
  extraStyles?: boolean;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
}

const isInt = (value): boolean => /^[0-9]\d*$/.test(value);

const InputControl = (props: Props): JSX.Element => {
  const {
    id,
    errorId,
    ariaLabel,
    errorAriaLabel,
    name,
    placeholder,
    value,
    label,
    onChange,
    type = 'text',
    errors = [],
    disabled = false,
    showInlineHelp = false,
    onValueChange,
    onBlur,
    className,
    withMinimumValue,
    helperText,
    onSubmit,
    extraStyles,
    min,
    inputMode,
  } = props;

  const [inputValue, setInputValue] = useState<string | number>(!value ? '' : value);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isLabelFloated, setIsLabelFloated] = useState<boolean>(false);
  const [infoPopover, setInfoPopover] = useState(null);

  const handlePopoverClick = (event: any) => setInfoPopover(event.currentTarget);
  const handlePopoverClose = () => setInfoPopover(null);

  const minValue = typeof min === 'number' ? min : 1;
  const isInteger = useCallback(isInt, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;
    let targetValue = target.value;

    if (type === 'number') {
      if (
        withMinimumValue
        && (!isInteger(targetValue) || (isInteger(targetValue) && parseInt(targetValue, 10) < minValue))
      ) {
        targetValue = minValue.toString();
      } else if (isInteger(targetValue)) {
        targetValue = parseInt(targetValue, 10).toString();
      }

      target.value = targetValue;
    }

    setInputValue(targetValue);

    onChange && onChange(event);
    onValueChange && onValueChange(targetValue);
  }, [type, onChange, onValueChange, withMinimumValue, isInteger, minValue]);

  const handleFocus = useCallback((): void => {
    setIsFocused(true);
    setIsLabelFloated(true);
  }, []);

  const handleOnKeyPress = useCallback((event: any): boolean | void => {
    const target = event.target as HTMLInputElement;
    const { key } = event;

    const targetValue = target.value + key;

    if ((type === 'number') && !isInteger(targetValue)) {
      event.preventDefault();
    }

    key === 'Enter' && onSubmit?.();
  }, [isInteger, type, onSubmit]);

  const handleBeforeInput = useCallback((event: FormEvent<HTMLInputElement>): void => {
    if (type !== 'number') return;
    const inputEvent = event.nativeEvent as InputEvent;
    if (inputEvent.data === '.') {
      event.preventDefault();
    }
  }, [type]);

  const handleBlur = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement;

    let targetValue = target.value;

    const noWhitespacesCheck = targetValue.trim() !== '';

    setIsFocused(false);
    setIsLabelFloated(noWhitespacesCheck);

    if (!noWhitespacesCheck) {
      targetValue = '';
      setInputValue('');

      target.value = targetValue;

      onChange && onChange(event);
    }

    onBlur && onBlur(targetValue);
  }, [onChange, onBlur]);

  useEffect(() => {
    if (value) {
      setInputValue(value);
      setIsLabelFloated(true);
    } else {
      setIsLabelFloated(false);
    }

    return () => {
      setInputValue('');
    };
  }, [inputValue, value, setInputValue]);

  const hasError = errors.length > 0;

  return (
    <div className={className}>
      <div
        className={[
          styles.inputContainer,
          styles.inputContainerBg,
          disabled && styles.disabled,
          !label && styles.inputContainerSecondary,
          extraStyles && styles.extraInputContainerSecondary,
          extraStyles && isFocused && styles.extraInputContainer,
          extraStyles && hasError && styles.inputExtraErrorControlBorder,
        ].join(' ')}
      >
        <input
          id={id}
          aria-label={ariaLabel}
          autoComplete="off"
          type={type}
          min={min}
          name={name}
          value={inputValue}
          inputMode={inputMode}
          disabled={disabled}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleChange}
          onBeforeInput={handleBeforeInput}
          className={[
            styles.input,
            styles.inputControlBorder,
            showInlineHelp && styles.secondaryInput,
          ].join(' ')}
          placeholder={placeholder}
          onKeyPress={handleOnKeyPress}
        />

        {(
          label && (
            <label
              htmlFor={id}
              className={[styles.label, (isLabelFloated || isFocused) && styles.floatedLabel].join(' ')}
            >
              {label}
            </label>
          )
        )}

        {showInlineHelp && (
          <>

            <button
              type="button"
              className={styles.groupAddon}
              onClick={handlePopoverClick}
            >
              <img id="infoIcon" aria-label={`${label} info icon`} src={InfoIcon} alt="exclamation mark" />
            </button>
            <Popover
              open={Boolean(infoPopover)}
              anchorEl={infoPopover}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
              classes={{ paper: styles.popoverPaper }}
            >
              <p>
                {lang.commonOnboardingPersonalDetailsTaxNumberMessage()}
              </p>
            </Popover>
          </>
        )}
      </div>

      {helperText && <small className={styles.helper}>{helperText}</small>}
      {errors.length > 0 && (
        <>
          {extraStyles
            ? (
              <div className={styles.extraErrorContainer}>
                <img src={InputErrorIcon} alt="!" />
                <p key={errors[0].id} id={errorId} aria-label={errorAriaLabel} className={[styles.error, styles.extraError].join(' ')}>
                  {errors[0].error}
                </p>
              </div>
            )
            : (
              <p key={errors[0].id} id={errorId} aria-label={errorAriaLabel} className={styles.error}>
                {errors[0].error}
              </p>
            )}
        </>
      )}
    </div>
  );
};

export default InputControl;
